/**
 * asyncapi-devtools — vuelca en la consola de DevTools la INFORMACIÓN DE CONEXIÓN del
 * organismo vía AsyncAPI 3.0 (el bus reactivo §33). Hermano de `agent-guide.full`:
 * aquél da el "qué razona" de cada entidad; éste da el "cómo se conecta" (quién publica
 * qué evento, quién lo escucha — inversión emisor/oyente §10).
 *
 * Fuente: GET /api/v1/docs/asyncapi.json?scope=mio (filtrado por el CO del JWT §32):
 *   - servers     → el bus (URL + protocolo del transporte reactivo).
 *   - channels    → 1 por entidad+fase: "eventos publicados cuando una instancia entra
 *                   en esa fase". El address es `<entidad>.<fase>`.
 *   - operations  → send/receive sobre esos canales (publicar / suscribirse).
 *   - x-aarpia-meta → doctrina + transporte.
 *
 * Dos vistas (el operador pidió "el máximo de información"):
 *   1. BUS GLOBAL del CO — al cargar la app (una vez por sesión), colapsado para no saturar.
 *   2. POR ENTIDAD — al abrir una entidad, su conexión específica (qué publica + operaciones).
 *
 * Además expone `window.aarpia` para explorar a mano desde la consola.
 */

interface AsyncapiSpec {
  asyncapi: string
  info?: { title?: string; version?: string; description?: string }
  servers?: Record<string, any>
  channels?: Record<string, any>
  operations?: Record<string, any>
  components?: Record<string, any>
  'x-aarpia-meta'?: any
}

let specCache: AsyncapiSpec | null = null
let yaLogueadoBus = false
// Entidad pendiente de volcar: si se abre una entidad (EntidadDetallePage) ANTES de que la
// spec del bus haya cargado (carga en frío / URL directa / hard-reload), se encola aquí y se
// vuelca en cuanto `inicializarAsyncapiDevtools` puebla specCache. Sin esto el dump por-entidad
// hacía no-op silencioso en frío (la "info de conexión" no aparecía salvo navegación warm).
let entidadPendiente: string | null = null

const ESTILO_TITULO = 'color:#0d9488;font-weight:bold;font-size:12px'
const ESTILO_SUB = 'color:#14b8a6;font-weight:bold'
const ESTILO_TENUE = 'color:#94a3b8'

/** Address de canal `<entidad>.<fase>` → { entidad, fase }. */
function partirAddress(address: string): { entidad: string; fase: string } {
  const i = address.lastIndexOf('.')
  return i < 0 ? { entidad: address, fase: '' } : { entidad: address.slice(0, i), fase: address.slice(i + 1) }
}

function resumenCanales(spec: AsyncapiSpec) {
  return Object.values(spec.channels || {}).map((c: any) => {
    const { entidad, fase } = partirAddress(c.address || '')
    return { entidad, fase, address: c.address, descripcion: (c.description || '').slice(0, 80) }
  })
}

/** Imprime el BUS GLOBAL del CO (colapsado). `scoped` = la spec viene filtrada §32 del CO. */
export function logBusGlobal(spec: AsyncapiSpec, scoped = true) {
  const canales = resumenCanales(spec)
  const servers = spec.servers || {}
  const nOps = Object.keys(spec.operations || {}).length
  // meta.total_channels es el conteo GLOBAL; bajo scope=mio la spec trae menos canales
  // (los del CO). Mostrar "N de M" para que el header (filtrado) no parezca contradecir su meta.
  const totalGlobal = spec['x-aarpia-meta']?.total_channels
  const etiqueta = scoped && totalGlobal && totalGlobal !== canales.length
    ? `${canales.length} de ${totalGlobal} canales (filtrado §32 de tu CO) · ${nOps} operaciones`
    : `${canales.length} canales · ${nOps} operaciones`
  /* eslint-disable no-console */
  console.groupCollapsed(
    `%cAARPIA · Conexión del organismo (AsyncAPI ${spec.asyncapi})%c  ${etiqueta}`,
    ESTILO_TITULO, ESTILO_TENUE,
  )
  console.log(`%c${spec.info?.title || 'Bus reactivo'} %cv${spec.info?.version || '?'}`, ESTILO_SUB, ESTILO_TENUE)
  if (spec.info?.description) console.log('%c' + spec.info.description, ESTILO_TENUE)

  console.groupCollapsed('%cServidores (el bus)', ESTILO_SUB)
  console.table(
    Object.entries(servers).map(([nombre, s]: [string, any]) => ({
      servidor: nombre, host: s.host, protocolo: s.protocol, pathname: s.pathname,
    })),
  )
  console.groupEnd()

  console.groupCollapsed(`%cCanales pub/sub (${canales.length})`, ESTILO_SUB)
  console.table(canales)
  console.groupEnd()

  if (spec['x-aarpia-meta']) {
    console.groupCollapsed('%cMeta AARPIA (doctrina + transporte)', ESTILO_SUB)
    console.log(spec['x-aarpia-meta'])
    console.groupEnd()
  }

  console.log('%cspec completa →', ESTILO_TENUE, spec)
  console.log('%cExplora: window.aarpia.salud() (semáforo REST+SSE+MCP) · window.aarpia.entidad("<nombre>") · window.aarpia.canales() · window.aarpia.asyncapi', ESTILO_TENUE)
  console.groupEnd()
  /* eslint-enable no-console */
}

/** Imprime la conexión de UNA entidad: qué publica (sus fases) + operaciones. §10. */
export function logConexionEntidad(nombreEntidad: string) {
  // Carga en frío: la spec aún no llegó → encolar y volcar cuando esté lista (no perder el dump).
  if (!specCache) { entidadPendiente = nombreEntidad; return }
  const canales = Object.values(specCache.channels || {}).filter((c: any) =>
    (c.address || '').startsWith(nombreEntidad + '.'),
  )
  if (canales.length === 0) return
  const ops = Object.entries(specCache.operations || {}).filter(([, o]: [string, any]) => {
    const ref = o?.channel?.$ref || ''
    return ref.includes(encodeURIComponent(nombreEntidad)) || ref.includes(nombreEntidad)
  })
  /* eslint-disable no-console */
  console.groupCollapsed(
    `%cAARPIA · Conexión de ${nombreEntidad} (AsyncAPI)%c  publica en ${canales.length} fases`,
    ESTILO_TITULO, ESTILO_TENUE,
  )
  console.log('%cPublica eventos al entrar en estas fases (otros COs/entidades pueden escucharlas — §10):', ESTILO_TENUE)
  console.table(canales.map((c: any) => {
    const { fase } = partirAddress(c.address || '')
    return { fase, address: c.address, descripcion: (c.description || '').slice(0, 100) }
  }))
  if (ops.length) {
    console.groupCollapsed(`%cOperaciones (${ops.length})`, ESTILO_SUB)
    console.table(ops.map(([k, o]: [string, any]) => ({ operacion: k, accion: o.action, canal: o?.channel?.$ref?.split('/').pop() })))
    console.groupEnd()
  }
  console.groupEnd()
  /* eslint-enable no-console */
}

/**
 * Token actual desde el store persistido (para que `window.aarpia.salud()` sea
 * autocontenido en la consola, sin pasar argumentos).
 */
function tokenActual(): string | null {
  try {
    return JSON.parse(localStorage.getItem('arquitecto-auth') || '{}')?.state?.token || null
  } catch {
    return null
  }
}

/**
 * Sondea la SALUD de TODAS las conexiones del cliente con el organismo. Cuatro
 * transportes auditados:
 *   - REST `/api/v1/health` — la API viva.
 *   - REST `/api/v1/canonico` — lectura/escritura de entidades (cuántas ve el CO).
 *   - SSE `/api/v1/eventos` — el bus reactivo §33.2 (el ÚNICO streaming que el
 *     front consume; `useEventStream`). Mide tiempo de conexión + readyState.
 *   - MCP `/api/v1/mcp` — Streamable HTTP (JSON-RPC enmarcado en SSE). El
 *     organismo lo expone server-side; el FRONT NO lo consume (es agent-only),
 *     pero se sondea para auditar que está sano.
 */
// ── Sondas independientes (fix DC-2, orden 8f3a2596 — bug cazado por el auditor) ────
//
// El bug: `await r.text()` sobre la respuesta MCP Streamable (SSE-framed, el servidor
// MANTIENE el stream abierto) no resolvía jamás → la cadena secuencial se colgaba en
// MCP, el agregado nunca volvía Y cada sonda colgada FILTRABA un stream abierto sin
// abort — con el SSE del chat ya abierto, el pool por-host del navegador (~6 conexiones
// HTTP/1.1) se agotaba y el siguiente GET /health quedaba en pending perpetuo.
// El fix: cada sonda es INDEPENDIENTE, acotada por timeout, y CIERRA su recurso
// (abort del stream MCP, close del EventSource). Corren en paralelo. Ninguna rechaza.

const TIMEOUT_SONDA_MS = 6000

async function fetchAcotado(url: string, init: RequestInit, ms = TIMEOUT_SONDA_MS): Promise<Response> {
  const ctl = new AbortController()
  const t = setTimeout(() => ctl.abort(), ms)
  try { return await fetch(url, { ...init, signal: ctl.signal }) } finally { clearTimeout(t) }
}

export async function probeHealth(H: Record<string, string>) {
  const s = performance.now()
  try {
    const r = await fetchAcotado('/api/v1/health', { headers: H })
    return { ok: r.ok, status: r.status, ms: Math.round(performance.now() - s) }
  } catch (e: any) { return { ok: false, error: e?.name === 'AbortError' ? `timeout (${TIMEOUT_SONDA_MS / 1000}s)` : e?.message || String(e) } }
}

export async function probeCanonico(tk: string | null, H: Record<string, string>) {
  if (!tk) return { ok: false, error: 'sin token (omitido — no se dispara /canonico sin Authorization)' }
  const s = performance.now()
  try {
    const r = await fetchAcotado('/api/v1/canonico', { headers: H })
    const j = await r.json().catch(() => ({}))
    return { ok: r.ok, status: r.status, ms: Math.round(performance.now() - s), entidades: j?.meta?.total_entidades_visibles, subdominios: j?.meta?.subdominios }
  } catch (e: any) { return { ok: false, error: e?.name === 'AbortError' ? `timeout (${TIMEOUT_SONDA_MS / 1000}s)` : e?.message || String(e) } }
}

export async function probeMcp(tk: string | null, H: Record<string, string>) {
  if (!tk) return { ok: false, error: 'sin token (omitido)' }
  const s = performance.now()
  const ctl = new AbortController()
  const t = setTimeout(() => ctl.abort(), TIMEOUT_SONDA_MS)
  try {
    const r = await fetch('/api/v1/mcp', {
      method: 'POST',
      headers: { ...H, 'Content-Type': 'application/json', Accept: 'application/json, text/event-stream' },
      body: JSON.stringify({ jsonrpc: '2.0', method: 'initialize', params: { protocolVersion: '2024-11-05', capabilities: {}, clientInfo: { name: 'aarpia-devtools', version: '1' } }, id: 1 }),
      signal: ctl.signal,
    })
    // fetch resuelve con los HEADERS: status y content-type ya son ciertos aquí. El BODY
    // puede ser un stream que nunca cierra — se lee con carrera corta y, si no llega, se
    // ABORTA (libera la conexión del pool; teníamos lo útil).
    const ms = Math.round(performance.now() - s)
    const ct = r.headers.get('content-type') || ''
    let server: any = null
    const body = await Promise.race([
      r.text().catch(() => null),
      new Promise<null>((res) => setTimeout(() => res(null), 1500)),
    ])
    if (body === null) ctl.abort() // stream abierto: cortar, no filtrar la conexión
    else {
      const m = body.match(/"serverInfo":(\{[^}]*\})/)
      if (m) { try { server = JSON.parse(m[1]) } catch { /* ignore */ } }
    }
    return { ok: r.ok, status: r.status, ms, transporte: ct.includes('event-stream') ? 'streamable-http (SSE-framed)' : ct, server, consumido_por_front: false }
  } catch (e: any) {
    return { ok: false, error: e?.name === 'AbortError' ? `timeout (${TIMEOUT_SONDA_MS / 1000}s)` : e?.message || String(e) }
  } finally { clearTimeout(t) }
}

export async function probeSse(tk: string | null) {
  const s = performance.now()
  return new Promise<any>((resolve) => {
    if (!tk) return resolve({ ok: false, error: 'sin token' })
    let connectMs: number | null = null
    const es = new EventSource(`/api/v1/eventos?token=${encodeURIComponent(tk)}`)
    es.onopen = () => { connectMs = Math.round(performance.now() - s) }
    setTimeout(() => {
      const rs = es.readyState
      es.close()
      resolve({ ok: connectMs != null, connect_ms: connectMs, readyState: rs, estado: rs === 1 ? 'OPEN' : rs === 0 ? 'CONNECTING' : 'CLOSED' })
    }, 2500)
  })
}

export async function probarConexiones(token: string | null) {
  // H4 (orden 3d2f73fd): nunca disparar un endpoint autenticado SIN token (daría un 401 ruidoso
  // en la pestaña Red). Se deriva del store si el caller no lo pasó; los probes con auth se omiten
  // si no hay sesión. /health es público y se sondea siempre.
  const tk = token ?? tokenActual()
  const H: Record<string, string> = tk ? { Authorization: 'Bearer ' + tk } : {}
  // PARALELO y acotado: ninguna sonda bloquea a las demás; el agregado vuelve siempre
  // en ≤ TIMEOUT_SONDA_MS (ninguna sonda rechaza — devuelven {ok:false, error}).
  const [rest_health, rest_canonico, mcp_streamable, sse_eventos] = await Promise.all([
    probeHealth(H), probeCanonico(tk, H), probeMcp(tk, H), probeSse(tk),
  ])
  return { rest_health, rest_canonico, mcp_streamable, sse_eventos }
}

/** Vuelca la salud de conexiones en consola con semáforo (auditoría de un vistazo). */
export async function logSaludConexiones(token: string | null = tokenActual()) {
  const r = await probarConexiones(token)
  const sem = (ok?: boolean) => (ok ? '🟢' : '🔴')
  /* eslint-disable no-console */
  console.groupCollapsed('%cAARPIA · Salud de conexiones con el organismo', ESTILO_TITULO)
  console.table({
    'REST /health': { estado: sem(r.rest_health?.ok), detalle: r.rest_health?.status ?? r.rest_health?.error, ms: r.rest_health?.ms },
    'REST /canonico (entidades)': { estado: sem(r.rest_canonico?.ok), detalle: r.rest_canonico?.ok ? `${r.rest_canonico?.entidades} ent · ${r.rest_canonico?.subdominios} sub` : r.rest_canonico?.error, ms: r.rest_canonico?.ms },
    'SSE /eventos (bus, el front SÍ stremea)': { estado: sem(r.sse_eventos?.ok), detalle: r.sse_eventos?.estado ?? r.sse_eventos?.error, ms: r.sse_eventos?.connect_ms },
    'MCP /mcp (streamable, server-side)': { estado: sem(r.mcp_streamable?.ok), detalle: r.mcp_streamable?.ok ? r.mcp_streamable?.transporte : r.mcp_streamable?.error, ms: r.mcp_streamable?.ms },
  })
  if (r.mcp_streamable?.server) console.log('%cMCP serverInfo (el organismo lo expone; el front NO lo consume — es agent-only):', ESTILO_TENUE, r.mcp_streamable.server)
  console.log('%cEl único streaming que el cliente consume es SSE /eventos (useEventStream). MCP es para agentes.', ESTILO_TENUE)
  console.groupEnd()
  /* eslint-enable no-console */
  return r
}

/** Carga (una vez) la spec del CO, loguea el bus global y arma window.aarpia.
 *  Idealmente se llama cuando el CO ya está resuelto (App.tsx gatea token+CO); aun así,
 *  si `?scope=mio` falla (p.ej. CO a medio resolver → 403) se cae al spec GLOBAL sin
 *  reintentar en bucle (antes el guard se reseteaba y disparaba 3× 403 en consola). */
export async function inicializarAsyncapiDevtools(token: string | null) {
  if (yaLogueadoBus || !token) return
  yaLogueadoBus = true
  try {
    const headers = { Authorization: `Bearer ${token}`, Accept: 'application/json' }
    let scoped = true
    let r = await fetch('/api/v1/docs/asyncapi.json?scope=mio', { headers })
    if (!r.ok) {
      // Fallback al bus global (sin scope): evita el bucle de 403 y siempre muestra algo.
      scoped = false
      r = await fetch('/api/v1/docs/asyncapi.json', { headers })
    }
    if (!r.ok) { yaLogueadoBus = false; return } // ambos fallaron → permitir un reintento futuro
    const spec: AsyncapiSpec = await r.json()
    specCache = spec

    // window.aarpia — explorador de conexión a mano desde la consola.
    ;(window as any).aarpia = {
      asyncapi: spec,
      bus: () => logBusGlobal(spec, scoped),
      canales: () => { /* eslint-disable-next-line no-console */ console.table(resumenCanales(spec)); return resumenCanales(spec) },
      entidad: (nombre: string) => logConexionEntidad(nombre),
      // Auditoría de salud de TODAS las conexiones (REST + SSE + MCP streamable) con semáforo.
      salud: () => logSaludConexiones(),
      conexiones: () => probarConexiones(tokenActual()),
      eventosUrl: '/api/v1/eventos', // SSE — transporte reactivo en vivo (§33.2), el front SÍ lo consume
      mcpUrl: '/api/v1/mcp', // MCP Streamable HTTP — server-side, agent-only (el front NO lo consume)
    }

    logBusGlobal(spec, scoped)
    // Volcar la conexión de la entidad que se abrió antes de que cargara la spec (carga en frío).
    if (entidadPendiente) { const n = entidadPendiente; entidadPendiente = null; logConexionEntidad(n) }
    // Auto-volcar la SALUD de transportes (REST + SSE + MCP streamable HTTP) — así el
    // streamable se ve de un vistazo sin teclear window.aarpia.salud() (lo pidió el operador).
    void logSaludConexiones(token)
  } catch {
    yaLogueadoBus = false
  }
}
